import muteOn from '../../assets/mute-on.svg'
import muteOff from '../../assets/mute-off.svg'
import { addClass } from '../helpers/DOM'
import { PubSub } from '../helpers/pubSub'
import { getIntersectionObserver } from '../helpers/Observer'
import { doOnce } from '../helpers/doOnce'
import { PlayerState } from '../enums/PlayerState'

const muteOnHtml = `
    <span class='mv-mute-button' role='button' aria-label='Unmute ad' >
        <object id="iconUnmuteAd" data=${muteOn} type="image/svg+xml">
          Unmute Ad
        </object>
      </span>
  `

const muteOffHtml = `
  <span class='mv-mute-button' role='button' aria-label='Mute ad' >
      <object id="iconMuteAd" data=${muteOff} type="image/svg+xml">
        Mute Ad
      </object>
    </span>
`
export interface IPlayerConfig {
  container: HTMLElement
  height: number
  width: number
  autoplay: boolean
  muted: boolean
}

export enum PlayerEvent {
  adError = 'adError',
  adsCompleted = 'adsCompleted',
  started = 'started',
  impression = 'impression',
  visibilityChange = 'visibilityChange'
}

/**
 * Base Class for Ad Players
 * Extendable to use external libraries like video.js, IMA, etc.
 */
export abstract class AdPlayer {
  readonly events = PlayerEvent
  protected muteButton: HTMLElement
  protected muted: boolean
  protected eventManager: PubSub<typeof PlayerEvent> = new PubSub<
    typeof PlayerEvent
  >()
  protected _playerState: PlayerState = PlayerState.init
  protected isVisible: boolean = false

  constructor(protected readonly config: IPlayerConfig) {
    this.muted = config.muted
    this.muteButton = this.createMuteButton(config.container, config.muted)
    this.createViewportObserver()
  }

  get hasNotBeenOnScreen(): boolean {
    return this.playerState !== PlayerState.resume && this.isVisible === false
  }

  get playerState(): PlayerState {
    return this._playerState
  }

  onEvent(event: PlayerEvent, cb: Function) {
    this.eventManager.on(event, cb)
  }

  abstract initVideoAd({
    vastXml,
    vastUrl
  }: Partial<{ vastXml; vastUrl }>): void
  abstract cleanup(): void
  abstract play(): void
  abstract pause(): void

  enableViewablePlayMode = doOnce(() => {
    this.onEvent(PlayerEvent.visibilityChange, (visible) => {
      if (this.playerState === PlayerState.init) {
        return
      }
      visible ? this.play() : this.pause()
    })

    this.onEvent(PlayerEvent.started, () => {
      setTimeout(() => {
        if (this.hasNotBeenOnScreen) {
          this.pause()
        }
      }, 1000)
    })
  })

  protected handleMuteClick(): void {
    if (this.muted) {
      this.muteButton.innerHTML = muteOffHtml
    } else {
      this.muteButton.innerHTML = muteOnHtml
    }

    this.muted = !this.muted
  }

  protected createMuteButton(container: HTMLElement, muted: boolean) {
    const muteButton = document.createElement('div')
    muteButton.innerHTML = muted ? muteOnHtml : muteOffHtml
    muteButton.style.display = 'none'
    muteButton.addEventListener('click', this.handleMuteClick.bind(this))
    addClass(muteButton, 'mv-outstream-mute')
    muteButton.style.display = 'block'
    container.insertAdjacentElement('afterbegin', muteButton)

    return muteButton
  }

  protected mute() {
    this.muteButton.innerHTML = muteOnHtml
    this.muted = true
  }

  private createViewportObserver({ config: { container } } = this) {
    const config = { rootMartin: `0px 0px 0px 0px`, threshold: 0.51 }
    getIntersectionObserver().onChange(this.onViewportChangeHandler(), config)
    getIntersectionObserver().observe(container, config)
  }

  private onViewportChangeHandler = ({ config: { container } } = this) => (
    entries
  ) => {
    entries.forEach((entry) => {
      if (entry.target !== container) {
        return
      }
      this.isVisible = entry.isIntersecting
      this.eventManager.trigger(PlayerEvent.visibilityChange, this.isVisible)
    })
  }
}
